import { type Chat, useChat } from '@ai-sdk/react';
import { type CrowdexUIMessage, failInterruptedMessages } from '@crowdex/agent-contracts';
import { useCallback, useEffect, useRef } from 'react';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';

/** At most one render of a streaming conversation per this many milliseconds. */
const CHAT_RENDER_THROTTLE_MS = 50;

/** Shared React binding: resume server-owned turns in both local and cloud profiles. */
export function useCrowdexChat(
  chat: Chat<CrowdexUIMessage>,
  cloudRuntime: CloudRuntime | null | undefined,
  serverPersisted = false,
  resumeEnabled = true,
) {
  const shouldResume =
    resumeEnabled && (serverPersisted || (cloudRuntime !== null && cloudRuntime !== undefined));
  // A message sent while a resume is in flight waits for it: the resume settling (often with
  // nothing to resume) would otherwise mark the chat idle while the new turn runs.
  const resuming = useRef<Promise<void>>(Promise.resolve());
  // The SDK's resume effect depends on its stable ref, so it does not run when
  // hydration replaces the placeholder Chat with a working transport.
  useEffect(() => {
    if (shouldResume && chat.status !== 'submitted' && chat.status !== 'streaming') {
      resuming.current = chat.resumeStream().catch(() => undefined);
    }
  }, [chat, shouldResume]);
  // Streamed chunks can land in bursts (after an awaited tool, a resume): rendering each one
  // synchronously would nest past React's update limit.
  const binding = useChat<CrowdexUIMessage>({ chat, throttle: CHAT_RENDER_THROTTLE_MS });
  const send = binding.sendMessage;
  const sendMessage = useCallback(
    async (...args: Parameters<typeof send>) => {
      await resuming.current;
      return await send(...args);
    },
    [send],
  ) as typeof send;
  const stop = useCallback(async () => {
    // The browser reader stops before it can receive the server's cancellation
    // updates. Settle that exact response locally without touching a newer turn.
    const messageId = chat.messages.at(-1)?.id;
    await chat.stop();
    settleResponse(chat, messageId);
  }, [chat]);
  // A turn that failed streams no more: its steps still open settle, as when it is stopped.
  useEffect(() => {
    if (binding.status === 'error') settleResponse(chat, chat.messages.at(-1)?.id);
  }, [binding.status, chat]);
  return { ...binding, sendMessage, stop };
}

function settleResponse(chat: Chat<CrowdexUIMessage>, messageId: string | undefined): void {
  chat.messages = chat.messages.map((message) =>
    message.id === messageId && message.role === 'assistant'
      ? (failInterruptedMessages([message])[0] ?? message)
      : message,
  );
}
